import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react'

import { RequestStatus } from 'core/domain/Request'

const enum Action {
  SetLaneErrors = 'SET_LANE_ERRORS',
  SetLastSaved = 'SET_LAST_SAVED',
  SetLaneProposalStatus = 'SET_LANE_PROPOSAL_STATE',
  SetUploadLanesFileStatus = 'SET_UPLOAD_LANES_FILE_STATUS',
  SetLaneProposalIds = 'SET_LANE_PROPOSAL_IDS',
}

type LaneErrors = {
  hasCapacityError: boolean
  hasRatePerMileError: boolean
  hasOutOfSyncError: boolean
}

type State = {
  laneErrors: LaneErrors
  lastSavedDate?: Date
  submitLaneProposalStatus: RequestStatus
  uploadLanesFileStatus: RequestStatus
  laneProposalIds: Array<number>
}

type StateAction = {
  type: Action
  payload: Partial<State>
}

function reducer(state: State, action: StateAction): State {
  const { type, payload } = action
  switch (type) {
    case Action.SetLaneErrors:
      return {
        ...state,
        laneErrors: payload.laneErrors!,
      }

    case Action.SetLastSaved:
      return {
        ...state,
        lastSavedDate: payload.lastSavedDate!,
      }

    case Action.SetLaneProposalStatus:
      return {
        ...state,
        submitLaneProposalStatus: payload.submitLaneProposalStatus!,
      }

    case Action.SetLaneProposalIds:
      return {
        ...state,
        laneProposalIds: payload.laneProposalIds!,
      }

    case Action.SetUploadLanesFileStatus:
      return {
        ...state,
        uploadLanesFileStatus: payload.uploadLanesFileStatus!,
      }

    default:
      throw new Error(`Trying to use an invalid action: ${action}`)
  }
}

export type RfpContextType = {
  state: State
  updateLaneProposalIds: (laneProposalIds?: number, shouldRemove?: boolean) => void
  setLaneProposalIds: (laneProposals: Array<number>) => void
  setLaneErrors: (errors: LaneErrors) => void
  setLastSaved: () => void
  setSubmitLaneProposalStatus: (status: RequestStatus) => void
  setUploadLanesFileStatus: (status: RequestStatus) => void
}

const initialState = {
  laneErrors: {
    hasCapacityError: false,
    hasRatePerMileError: false,
    hasOutOfSyncError: false,
  },
  uploadLanesFileStatus: RequestStatus.Initial,
  lastSavedDate: undefined,
  submitLaneProposalStatus: RequestStatus.Initial,
  laneProposalIds: [],
}

export const RfpContext = createContext<RfpContextType | undefined>(undefined)
RfpContext.displayName = 'RfpContext'

export function RfpProvider({ children }: { children?: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const setLaneErrors = useCallback((laneErrors: LaneErrors) => {
    dispatch({ type: Action.SetLaneErrors, payload: { laneErrors } })
  }, [])

  const setLastSaved = useCallback(() => {
    dispatch({ type: Action.SetLastSaved, payload: { lastSavedDate: new Date() } })
  }, [])

  const setSubmitLaneProposalStatus = useCallback((status: RequestStatus) => {
    dispatch({ type: Action.SetLaneProposalStatus, payload: { submitLaneProposalStatus: status } })
  }, [])

  const setUploadLanesFileStatus = useCallback((status: RequestStatus) => {
    dispatch({ type: Action.SetUploadLanesFileStatus, payload: { uploadLanesFileStatus: status } })
  }, [])

  const addLaneProposalId = useCallback(
    (laneProposalId: number) => {
      const index = state.laneProposalIds.indexOf(laneProposalId)

      if (index >= 0) return

      dispatch({
        type: Action.SetLaneProposalIds,
        payload: { laneProposalIds: [...state.laneProposalIds, laneProposalId] },
      })
    },
    [state.laneProposalIds]
  )

  const removeLaneProposalId = useCallback(
    (laneProposalId: number) => {
      const laneProposalIds = state.laneProposalIds.filter(
        lanePropId => lanePropId !== laneProposalId
      )

      dispatch({
        type: Action.SetLaneProposalIds,
        payload: {
          laneProposalIds,
        },
      })
    },
    [state.laneProposalIds]
  )

  const updateLaneProposalIds = useCallback(
    (laneProposalId?: number, shouldRemove: boolean = false) => {
      if (!laneProposalId) return

      if (shouldRemove) {
        removeLaneProposalId(laneProposalId)
      } else {
        addLaneProposalId(laneProposalId)
      }
    },
    [addLaneProposalId, removeLaneProposalId]
  )

  const setLaneProposalIds = useCallback((laneProposalIds: Array<number>) => {
    if (laneProposalIds.length > 0) {
      dispatch({
        type: Action.SetLaneProposalIds,
        payload: { laneProposalIds },
      })
    }
  }, [])

  const memoedValue = useMemo(
    () => ({
      state,
      setLaneProposalIds,
      updateLaneProposalIds,
      setLaneErrors,
      setLastSaved,
      setSubmitLaneProposalStatus,
      setUploadLanesFileStatus,
    }),
    [
      state,
      setLaneProposalIds,
      updateLaneProposalIds,
      setLaneErrors,
      setLastSaved,
      setSubmitLaneProposalStatus,
      setUploadLanesFileStatus,
    ]
  )

  return <RfpContext.Provider value={memoedValue}>{children}</RfpContext.Provider>
}

export function useRfpContext() {
  const context = useContext(RfpContext)

  if (context === undefined) {
    throw new Error(`useRfpContext must be used within a RfpProvider`)
  }

  return context
}
